import {
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useTab,
} from "@chakra-ui/react";
import axios from "axios";
import { endOfMonth, format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  BarChart2,
  Book,
  BookOpen,
  Briefcase,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Columns,
  CornerDownLeft,
  Cpu,
  CreditCard,
  File,
  MessageSquare,
  Table,
  Target
} from "react-feather";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fallbackDate } from "../../config/dashboardDefaults";
import { useCurrentProfile } from "../../ContextWrapper";
import { AccessToken } from "../../utils";
import { ChartOfAccounts } from "../account/chartOfAccounts";
import DashAdjustment from "../dashAdjustmentFeat/dashAdjustment";
import { GrowthPlan } from "../dashboard/growthPlan";
import { YearlyBudget } from "../dashboard/yearlybudget";
import PostCallDetailForm from "../postCallProfile/postCallProfile";
import CustomerRoyaltyFees from "../royalty/royaltyAdmin";
import TranscriptPanel from "../transcription/transcription";
import { ClientBankRules } from "./clientBankRules";
import { ClientBankTransactions } from "./clientBankTransactions";
import { ClientChat } from "./clientChat";
import { ClientDropDown } from "./clientDropDown";
import { ClientFileRequests } from "./clientFileRequests";
import { ClientGeneralLedger } from "./clientGeneralLedger";
import { ClientJournals } from "./clientJournals";
import { ClientPlaid } from "./clientPlaid";
import { ClientQuickbooks } from "./clientQuickbooks";
import { ClientReconciliations } from "./clientReconciliations";

// --- Tab registry and helpers (single source-of-truth, outside component) ---
const getUserLevel = (profile) =>
  profile?.type === "admin"
    ? "admin"
    : profile?.hasAccountingAccess
    ? "accountant"
    : "client";


const canSee = (userLevel, customer, tab) => {
  if (!tab.allowedLevels.includes(userLevel)) return false;
  if (tab.requiresNonGroupCustomer && customer?.customer_type === "group")
    return false;
  return true;
};

export const ClientDetail = () => {
  const [customer, setCustomer] = useState({});
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardDate, setDashboardDate] = useState(
    customer?.dashboard_date
      ? (() => {
          const d = endOfMonth(parseISO(customer.dashboard_date));
          return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        })()
      : fallbackDate
  );

  const defaultDashboardDate = React.useMemo(() => {
    const baseDate = customer?.dashboard_date
      ? parseISO(customer.dashboard_date)
      : fallbackDate;
    const eom = endOfMonth(baseDate);
    return new Date(eom.getFullYear(), eom.getMonth(), eom.getDate());
  }, [customer?.dashboard_date]);

  const [selectedDateCategory, setSelectedDateCategory] = useState(
    format(
      customer?.dashboard_date
        ? (() => {
            const d = endOfMonth(parseISO(customer.dashboard_date));
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
          })()
        : (() => {
            const d = endOfMonth(fallbackDate);
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
          })(),
      "yyyy-MM"
    )
  );
  const { customer_id, tabSlug, conversationId } = useParams();
  const { profile } = useCurrentProfile();
  const navigate = useNavigate();

  // If route is /client/:customer_id/:tabSlug? we read the optional tabSlug above
  // Compute visible tabs from the static registry and current user/profile
  const visibleTabs = React.useMemo(
    () =>
      TAB_DEFS.filter((tab) => canSee(getUserLevel(profile), customer, tab)),
    [profile, customer]
  );

  const visibleTabSlugs = visibleTabs.map((t) => t.slug);

  // Prefer the explicit :tabSlug route param when present, otherwise fall back to the first visible tab.
  // Special case: if we have a conversationId, we're on the chat tab even without explicit tabSlug
  const last = React.useMemo(() => {
    if (tabSlug && visibleTabSlugs.includes(tabSlug)) return tabSlug;
    if (conversationId) return "chat"; // We're on /client/:id/chat/:conversationId
    return visibleTabSlugs[0];
  }, [tabSlug, conversationId, visibleTabSlugs]);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    // Find the index of the last-used tab (if any) among the visible tabs.
    const idx = visibleTabSlugs.indexOf(last);
    setActiveTab(idx >= 0 ? idx : 0);
  }, [last, visibleTabSlugs]);

  // Ensure the URL contains the tab slug. If the route omits it, replace the URL
  // with the default visible tab so the UI is always URL-driven.
  // BUT: Don't redirect if we have a conversationId (we're on /client/:id/chat/:conversationId)
  useEffect(() => {
    if (!tabSlug && !conversationId && visibleTabSlugs.length > 0 && !loading) {
      // Only navigate if we're not currently loading customer data
      navigate(`/client/${customer_id}/${visibleTabSlugs[0]}`, {
        replace: true,
      });
    }
  }, [
    tabSlug,
    conversationId,
    visibleTabSlugs,
    navigate,
    customer_id,
    loading,
  ]);

  const handleTabChange = (index) => {
    const tab = visibleTabs[index];
    if (!tab) return;

    if (tab.slug === "dashboard") {
      sessionStorage.setItem("selectedCustomerId", customer.id.toString());
      window.dispatchEvent(new CustomEvent("selectedCustomerId-changed"));
      navigate(`/growth-plan`);
    } else {
      navigate(`/client/${customer_id}/${tab.slug}`);
    }

    setActiveTab(index);
  };

  // Fetch customer data only once when customer_id changes
  useEffect(() => {
    console.log("ClientDetail: Starting to fetch customer data");
    let accessToken = AccessToken();
    if (accessToken === null) {
      console.log("ClientDetail: No access token found, redirecting to login");
      window.location.href = "/login";
      return;
    }

    // Prevent duplicate calls by checking if we already have this customer loaded
    if (customer?.id === customer_id) {
      console.log(
        "ClientDetail: Customer data already loaded for ID:",
        customer_id
      );
      return;
    }

    console.log("ClientDetail: Fetching customer data for ID:", customer_id);
    setLoading(true);
    axios
      .get(
        `/api/v2/customers/${customer_id}/`,
        { headers: { "Content-Type": "application/json" } },
        { withCredentials: true }
      )
      .then((res) => {
        console.log("ClientDetail: Successfully fetched customer data");
        setCustomer(res.data);
        setDashboardDate(
          res.data.dashboard_date
            ? (() => {
                const d = endOfMonth(parseISO(res.data.dashboard_date));
                return new Date(d.getFullYear(), d.getMonth(), d.getDate());
              })()
            : fallbackDate
        );
        setSelectedDateCategory(
          format(
            res.data.dashboard_date
              ? (() => {
                  const d = endOfMonth(parseISO(res.data.dashboard_date));
                  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
                })()
              : (() => {
                  const d = endOfMonth(fallbackDate);
                  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
                })(),
            "yyyy-MM"
          )
        );
        setLoading(false);
      })
      .catch((err) => {
        console.error("ClientDetail: Error fetching customer data:", err);
        setError(err);
        setLoading(false);
      });
  }, [customer_id, customer?.id]);

  if (loading) {
    return (
      <Box p={4}>
        <Text>Loading customer details...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Text color="red.500">
          Error loading customer details. Please try again.
        </Text>
      </Box>
    );
  }

  // Simple ErrorBoundary to prevent a single panel from taking down the whole page
  class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }
    componentDidCatch(error, info) {
      // Log for debugging
      console.error(`Error in tab panel (${this.props.name}):`, error, info);

      // Report to Sentry with component context
      if (window.Sentry) {
        window.Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: info.componentStack,
              tabPanel: this.props.name,
            },
          },
        });
      }
    }
    render() {
      if (this.state.hasError) {
        return (
          <Box p={4}>
            <Text color="red.500">
              An error occurred loading this panel ({this.props.name}). Check
              console for details.
            </Text>
          </Box>
        );
      }
      return this.props.children;
    }
  }

  return (
    <Box position="relative">
      <Tabs
        orientation="vertical"
        isLazy
        index={activeTab}
        onChange={handleTabChange}
        display="flex"
        pt="80px"
        h="calc(100vh)"
        overflow="hidden"
      >
        <TabList
          border="0"
          bg="brand.1000"
          color="white"
          w={navCollapsed ? "72px" : "322px"}
          transition="width 0.2s ease"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Box>
            <Divider m="0" color="gray.50" />
            {!navCollapsed &&
              (customer ? <ClientDropDown customer={customer} /> : null)}
            {!navCollapsed && (
              <Box pl="6" pr="6">
                <Divider color="gray.50" />
              </Box>
            )}
            <HStack
              pl={navCollapsed ? "2" : "8"}
              pr="2"
              pt="4"
              mb="0"
              justify="space-between"
            >
              {!navCollapsed && (
                <Text fontSize="sm" color="gray.300">
                  Your Company
                </Text>
              )}
              <IconButton
                aria-label="Toggle sidebar"
                size="sm"
                variant="ghost"
                ml={navCollapsed ? 3 : 0}
                colorScheme="whiteAlpha"
                onClick={() => setNavCollapsed((v) => !v)}
                icon={navCollapsed ? <ChevronRight /> : <ChevronLeft />}
              />
            </HStack>
          </Box>

          <Box
            flex="1"
            overflowY="auto"
            px="0"
            mt="2"
            css={{
              scrollbarWidth: "thin",
              scrollbarColor: "#6B7280 transparent",
            }}
            overflowX={"hidden"}
            width={"100%"}
          >
            {visibleTabs.map((tab, idx) => (
              <React.Fragment key={tab.slug}>
                <CustomTab isCollapsed={navCollapsed} justifyContent="stretch">
                  <Box mr="2">{tab.icon}</Box>
                  {!navCollapsed ? tab.label : null}
                </CustomTab>
              </React.Fragment>
            ))}
          </Box>

          <Box pl="6" pb="3" pr="0">
            <Box mr="6">
              <Divider color="gray.50" />
            </Box>
            <Link to="/">
              <HStack
                pt="4"
                pr="6"
                w={navCollapsed ? "48px" : "270px"}
                justifyContent={navCollapsed ? "center" : "flex-start"}
              >
                <CornerDownLeft />
                {!navCollapsed && <Box>Back to Account Listing</Box>}
              </HStack>
            </Link>
            {/* )} */}
          </Box>
        </TabList>

        <TabPanels
          flex="1"
          overflowY="auto"
          h="calc(100vh - 80px)"
          bg="brand.50"
        >
          {visibleTabs.map((tab) => (
            <TabPanel key={tab.slug} p="0">
              <Box h="full">
                <ErrorBoundary name={tab.slug}>
                  {tab.render
                    ? tab.render({
                        customer,
                        dashboardDate,
                        setDashboardDate,
                        selectedDateCategory,
                        setSelectedDateCategory,
                        defaultDashboardDate,
                        conversationId,
                      })
                    : null}
                </ErrorBoundary>
              </Box>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const CustomTab = React.forwardRef(
  ({ isCollapsed, children, ...props }, ref) => {
    const tabProps = useTab({ ...props, ref });
    const isSelected = !!tabProps["aria-selected"];

    const childrenArray = React.Children.toArray(children);
    const iconEl = childrenArray[0];
    const labelText = childrenArray
      .slice(1)
      .map((c) => (typeof c === "string" ? c : ""))
      .join(" ")
      .trim();

    return (
      <Tooltip label={labelText} isDisabled={!isCollapsed} placement="right">
        <Button
          {...props}
          pt="6"
          pb="6"
          ml={isCollapsed ? "2" : "4"}
          mr={isCollapsed ? "2" : "4"}
          mt="2"
          mb="2"
          w={isCollapsed ? "48px" : "270px"}
          justifyContent={
            isCollapsed ? "center" : props.justifyContent || "flex-start"
          }
          bg={isSelected ? "brand.600" : "brand.1000"}
          aria-label={isCollapsed ? labelText : undefined}
          {...tabProps}
        >
          {isCollapsed ? iconEl : children}
        </Button>
      </Tooltip>
    );
  }
);

// Each tab uses a render(ctx) signature so panels receive everything they need
// from a small, explicit context instead of closing over component locals.
const TAB_DEFS = [
  {
    slug: "profile",
    label: "Profile",
    icon: <Clipboard />,
    allowedLevels: ["accountant", "admin"],
    render: (ctx) => <PostCallDetailForm customerId={ctx.customer.id} />,
  },
  {
    slug: "transactions",
    label: "Transactions",
    icon: <CreditCard />,
    allowedLevels: ["accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => <ClientBankTransactions customer_id={ctx.customer.id} />,
  },
  {
    slug: "plaid",
    label: "Plaid",
    icon: <Briefcase />,
    allowedLevels: ["client", "accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => <ClientPlaid customer_id={ctx.customer.id} />,
  },
  {
    slug: "chart-of-accounts",
    label: "Chart Of Accounts",
    icon: <Cpu />,
    allowedLevels: ["accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => (
      <ChartOfAccounts
        customer_id={ctx.customer.id}
        template={false}
        pt="0px"
        minH="0px"
      />
    ),
  },
  {
    slug: "bank-rules",
    label: "Bank Rules",
    icon: <CheckSquare />,
    allowedLevels: ["accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => <ClientBankRules customer_id={ctx.customer.id} />,
  },
  {
    slug: "journals",
    label: "Journals",
    icon: <BookOpen />,
    allowedLevels: ["accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => <ClientJournals customer_id={ctx.customer.id} />,
  },
  {
    slug: "general-ledger",
    label: "General Ledger",
    icon: <Table />,
    allowedLevels: ["accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => <ClientGeneralLedger customer_id={ctx.customer.id} />,
  },
  {
    slug: "reconciliation",
    label: "Reconciliation",
    icon: <Columns />,
    allowedLevels: ["accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => <ClientReconciliations customer_id={ctx.customer.id} />,
  },
  {
    slug: "quickbooks",
    label: "Quickbooks",
    icon: <Book />,
    allowedLevels: ["client", "accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => <ClientQuickbooks customer_id={ctx.customer.id} />,
  },
  {
    slug: "file-requests",
    label: "File Requests",
    icon: <File />,
    allowedLevels: ["client", "accountant", "admin"],
    requiresNonGroupCustomer: true,
    render: (ctx) => <ClientFileRequests customer_id={ctx.customer.id} />,
  },
  {
    slug: "dashboard",
    label: "Dashboard",
    icon: <BarChart />,
    allowedLevels: ["accountant", "admin"],
    render: (ctx) => <GrowthPlan />,
  },
  {
    slug: "targets",
    label: "Targets",
    icon: <Target />,
    allowedLevels: ["accountant", "admin"],
    render: (ctx) => <YearlyBudget customer_id={ctx.customer.id} />,
  },
  {
    slug: "adjustments",
    label: "Adjustments",
    icon: <BarChart2 />,
    allowedLevels: ["admin"],
    render: (ctx) => (
      <DashAdjustment
        customer_id={ctx.customer.id}
        dashDate={ctx.dashboardDate}
        updateDate={ctx.setDashboardDate}
        dateCategory={ctx.selectedDateCategory}
        setDateCategory={ctx.setSelectedDateCategory}
        initialDashDate={ctx.defaultDashboardDate}
      />
    ),
  },
  {
    slug: "royalty-fees",
    label: "Royalty Fees",
    icon: <BarChart2 />,
    allowedLevels: ["accountant", "admin"],
    render: (ctx) => (
      <CustomerRoyaltyFees
        customerId={ctx.customer.id}
        dashDate={ctx.dashboardDate}
        type={"profit"}
        updateDate={ctx.setDashboardDate}
        dateCategory={ctx.selectedDateCategory}
        setDateCategory={ctx.setSelectedDateCategory}
        initialDashDate={ctx.defaultDashboardDate}
      />
    ),
  },
  {
    slug: "chat",
    label: "Chat",
    icon: <MessageSquare />,
    allowedLevels: ["admin"],
    render: (ctx) => <ClientChat customerId={ctx.customer.id} />,
  },
  {
    slug: "transcripts",
    label: "Transcripts",
    icon: <Clipboard />,
    allowedLevels: ["admin"],
    render: (ctx) => <TranscriptPanel customerId={ctx.customer.id} />,
  },
];

// --- end registry ---
